import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Zap, WifiOff, CheckCircle } from 'lucide-react';

/**
 * PWAInstallPrompt - Enhanced component to prompt users to install the PWA
 * Shows a custom install banner with benefits when the app is installable
 */
const PWAInstallPrompt = ({ showToast }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      
      // Delay showing prompt to avoid being intrusive
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // Show after 3 seconds
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    setInstalling(true);

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        // Show success message
        setTimeout(() => {
          if (showToast) {
            showToast('App installed successfully! You can now access it from your home screen.', 'success');
          }
        }, 500);
      } else {
        console.log('User dismissed the install prompt');
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('Install error:', error);
    } finally {
      setInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowBenefits(false);
    // Don't show again for 7 days
    const dismissedUntil = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-install-dismissed-until', dismissedUntil.toString());
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    setShowBenefits(false);
    // Show again after 1 day
    const remindAt = Date.now() + (24 * 60 * 60 * 1000);
    sessionStorage.setItem('pwa-remind-at', remindAt.toString());
  };

  // Check if dismissed permanently or temporarily
  const dismissedUntil = localStorage.getItem('pwa-install-dismissed-until');
  if (dismissedUntil && Date.now() < parseInt(dismissedUntil)) {
    return null;
  }

  const remindAt = sessionStorage.getItem('pwa-remind-at');
  if (remindAt && Date.now() < parseInt(remindAt)) {
    return null;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <>
      {/* Compact Banner */}
      {!showBenefits && (
        <div className="fixed bottom-20 left-0 right-0 mx-4 bg-[#ff3f6c] text-white p-4 rounded shadow-2xl z-50 animate-slide-up">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="bg-white/20 p-2.5 rounded backdrop-blur-sm">
                  <Download className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-sm">Install DealStore App</h3>
                  <p className="text-xs text-[#94969f]">Quick access & offline features</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBenefits(true)}
                  className="text-xs text-[#94969f] hover:text-white underline transition-colors"
                >
                  Benefits
                </button>
                <button
                  onClick={handleInstallClick}
                  disabled={installing}
                  className="bg-white text-[#ff3f6c] px-4 py-2 rounded-lg text-sm font-bold hover:bg-[#fff8fa] transition-all shadow-lg disabled:opacity-50"
                >
                  {installing ? 'Installing...' : 'Install'}
                </button>
                <button
                  onClick={handleRemindLater}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Remind later"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Benefits Modal */}
      {showBenefits && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t sm:rounded max-w-lg w-full shadow-2xl animate-slide-up max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-[#ff3f6c] text-white p-6 rounded-t">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-2xl font-bold mb-1">Install DealStore App</h2>
                  <p className="text-[#94969f] text-sm">Get the best experience</p>
                </div>
                <button
                  onClick={() => setShowBenefits(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Benefits List */}
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-4 p-4 bg-[#fff8fa] rounded">
                <div className="bg-[#ff3f6c] p-3 rounded">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#282c3f] mb-1">Lightning Fast</h3>
                  <p className="text-sm text-[#535766]">
                    Instant loading with cached content for a smooth experience
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-[#e8f7f3] rounded">
                <div className="bg-[#03a685] p-3 rounded">
                  <WifiOff className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#282c3f] mb-1">Works Offline</h3>
                  <p className="text-sm text-[#535766]">
                    Browse products and view orders even without internet
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-[#fff8fa] rounded">
                <div className="bg-[#ff3f6c] p-3 rounded">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#282c3f] mb-1">Native App Feel</h3>
                  <p className="text-sm text-[#535766]">
                    Full-screen experience with smooth animations and gestures
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-[#fff8fa] rounded">
                <div className="bg-[#ff3f6c] p-3 rounded">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#282c3f] mb-1">No App Store Needed</h3>
                  <p className="text-sm text-[#535766]">
                    Install directly from browser - no downloads from stores
                  </p>
                </div>
              </div>

              {/* Install Info */}
              <div className="bg-[#fafafa] border border-[#eaeaec] rounded p-4">
                <p className="text-sm text-[#535766] text-center mb-2">
                  <strong>Quick & Easy:</strong> Takes only 2 MB of space
                </p>
                <p className="text-xs text-[#94969f] text-center">
                  You can uninstall anytime from your device settings
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-6 pt-0 space-y-3">
              <button
                onClick={handleInstallClick}
                disabled={installing}
                className="w-full text-white py-4 rounded font-bold text-base hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                {installing ? 'Installing App...' : 'Install App Now'}
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={handleRemindLater}
                  className="flex-1 bg-gray-100 text-[#535766] py-3 rounded font-semibold hover:bg-gray-200 transition-colors"
                >
                  Remind Me Later
                </button>
                <button
                  onClick={handleDismiss}
                  className="flex-1 bg-white border border-[#eaeaec] text-[#535766] py-3 rounded font-semibold hover:bg-gray-50 transition-colors"
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
